import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Language = 'sv' | 'en';

interface LanguageContextProps {
  language: Language;
  isSwedish: boolean; // Helper boolean for your UI logic
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 1. Initialize state from localStorage or default to Swedish
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLang = localStorage.getItem('app_lang');
    return (savedLang === 'en' || savedLang === 'sv') ? savedLang : 'sv';
  });

  // 2. Effect to update HTML tag and LocalStorage whenever language changes
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('app_lang', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === 'sv' ? 'en' : 'sv'));
  };

  const isSwedish = language === 'sv';

  return (
    <LanguageContext.Provider value={{ language, isSwedish, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for easy access
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};